import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppStateService } from 'core/app-state/app-state.service';
import { AppState } from 'core/common-entities/app-state.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { ApiService } from '../api/api.service';
import { UserInfo } from './user-info.class';
import {
  CountryCode,
  currencies,
  dateFormats,
  dateLocales,
  dateTimeFormats,
  dsDateFormats,
  timeFormat,
  weightUnits
} from './country-specifics.enum';

@Injectable({
  providedIn: 'root'
})
export class AppInfoService {
  private adminTypeRoles = ['IMPACT-Admin', 'IMPACT-CustomerService', 'IMPACT-FieldManager'];
  private accountManagerRoles = ['IMPACT-AccountManager'];
  state: AppState;
  appStrings: AppLocalization = {};
  userData: UserInfo;
  currencySymbol = '';
  weightSymbol = 'lbs';
  dateFormat = '';
  dateTimeFormat = '';
  readonly timeFormat = timeFormat;
  dsDateFormat = '';
  dateLocale = '';
  emptyGuid = '00000000-0000-0000-0000-000000000000';
  userDataChanged$ = new Subject<UserInfo>();
  countrySelected$ = new Subject();

  syncTemplateValues = {
    syncStatus: null,
    timerSyncStatus: null
  };
  urls = {
    onlineSyncStatusTemplateValues: '/api/appsettings/templatevalues',
    getOnlineSyncStatus: '/api/client/onlineSyncStatus',
    userInfo: '/api/identity/userinfo',
    validateOfflineUser: '/api/identity/offlineUser/validate',
    referenceToken: '/api/identity/referenceToken',
    authenticateOfflineUser: '/api/identity/offlineUser/authenticate',
    outlookSync: '/api/outlook/sync',
    initialOutlookSync: '/api/outlook/backgroundSync',
    selectCountry: '/api/identity/select/country',
    getOfflineUser: '/api/identity/offlineUser/getName',
  };

  constructor(
    private apiService: ApiService,
    private appStateService: AppStateService,
    private authenticationService: AuthenticationService,
  ) {
    this.state = this.appStateService.state;
    this.appStateService.offlineStateToggle$.subscribe(() => this.state = this.appStateService.state);
  }

  /**
   * Initializes application strings
   */
  initializeAppStrings() {
    return this.apiService.getAppStrings();
  }

  /**
   * Initializes user data
   */
  initializeUserData() {
    if (this.state.isElectron && !this.state.isOnline && !this.authenticationService.authenticatedOffline) {
      return this.apiService.get(this.urls.getOfflineUser, { responseType: 'text' });
    } else {
      return this.apiService.get(this.urls.userInfo);
    }
  }

  /**
   * Used for the initial sync of outlook contacts and touchpoints
   */
  startSynchOutlook() {
    sessionStorage.setItem('initialOutlookSyncStarted', 'true');
    return this.apiService.get(this.urls.initialOutlookSync);
  }

  /**
   * Retries synchronizing outlook contacts and touchpoints
   */
  retrySyncOutlook() {
    return this.apiService.get(this.urls.outlookSync);
  }

  /**
   * Validates user info after authentication
   */
  validateOfflineUserInfo(token: string) {
    return this.apiService.post(this.urls.validateOfflineUser, { enteredReferenceToken: token }, { responseType: 'text' });
  }

  /**
   * Sets the user info to be used for offline authentication
   */
  setOfflineUserInfo() {
    const userInfo = {
      UserId: this.userData.UserInfo.UserId,
      EmployeeId: this.userData.UserInfo.EmployeeId,
      Name: this.userData.UserInfo.Name,
      Roles: this.userData.roles
    };

    // get reference token and send it to /authenticate endpoint (uses concatMap to chain requests)
    return this.apiService.postGeneric(environment.offlineApiRoot + this.urls.authenticateOfflineUser,
      userInfo, true);
  }

  /**
   * Determines if the current user is Admin type
   */
  isAdminTypeUser() {
    let isAdmin = false;
    if (this.userData) {
      this.userData.roles.forEach((role: string) => {
        if ((this.adminTypeRoles.indexOf(role) !== -1)) {
          isAdmin = true;
        }
      });
    }
    return isAdmin;
  }

  isOnlyAccountManager() {
    let isAccountManager = false;
    let hasOtherRoles = false;
    if (this.userData) {
      this.userData.roles.forEach((role: string) => {
        if ((this.accountManagerRoles.indexOf(role) !== -1)) {
          isAccountManager = true;
        }
        if ((this.adminTypeRoles.indexOf(role) !== -1)) {
          hasOtherRoles = true;
        }
      });
    }
    return isAccountManager && !hasOtherRoles;
  }

  /**
   * Determines if the current user is Customer Service
   */
  isCustomerServiceTypeUser() {
    let isCustomerService = false;
    if (this.userData) {
      this.userData.roles.forEach((role: string) => {
        if (this.adminTypeRoles.indexOf(role) === 1) {
          isCustomerService = true;
        }
      });
    }
    return isCustomerService;
  }

  /**
   * Determines if the current user is Account Manager
   */
  isAccountManager() {
    let isAccountManager = false;
    if (this.userData) {
      this.userData.roles.forEach((role: string) => {
        if ((this.accountManagerRoles.indexOf(role) !== -1)) {
          isAccountManager = true;
        }
      });
    }
    return isAccountManager;
  }

  /**
   * Parses the list of modules and user access
   * @param userAccessObject - The initial list of modules
   */
  parseAccessAndModules(userAccessObject: any) {
    if (userAccessObject && typeof userAccessObject !== 'string') {
      const userAccess: UserInfo = new UserInfo();
      const { sub, ...claims } = userAccessObject.claims ?? {};
      userAccess.UserInfo = {
        ...claims,
        UserId: sub,
      };
      userAccess.modules = userAccessObject.modules || [];
      userAccess.roles = userAccessObject.roles || [];
      userAccess.countryCodes = userAccessObject.countryCodes || [];
      userAccess.countrySelection = Boolean(userAccessObject.countrySelection);
      return userAccess;
    } else if (typeof userAccessObject === 'string') {
      const userAccess: UserInfo = new UserInfo();
      userAccess.UserInfo = {
        Name: userAccessObject,
        EmployeeId: null,
        CountryCode: null,
        UserId: null,
      };
      userAccess.modules = [];
      userAccess.roles = [];
      userAccess.countryCodes = [];
      userAccess.countrySelection = false;
      return userAccess;
    } else {
      return null;
    }
  }

  /**
   * Checks if the user is Admin, Customer Service or AccountManager
   * Only used for the first phase
   */
  userTypeHasAccessForPhaseOne() {
    return this.isOnlyAccountManager() || this.isAdminTypeUser() || this.isCustomerServiceTypeUser();
  }

  /**
   * Checks if user has access to the application
   */
  userHasAccess() {
    if (!this.userData) {
      return false;
    }
    for (const module of this.userData.modules) {
      if (module.Key === 'impactmodule') {
        return true;
      }
    }
    return false;
  }

  /**
   * Gets the template values for receive updates from server
   */
  getOnlineSyncStatusTemplateValues(callback) {
    if (this.syncTemplateValues.syncStatus && this.syncTemplateValues.timerSyncStatus) {
      callback(this.syncTemplateValues);
      return;
    }

    this.apiService.getGeneric(environment.offlineApiRoot + this.urls.onlineSyncStatusTemplateValues, true)
      .subscribe(({ TimerSyncStatus, SyncStatus }) => {
        this.syncTemplateValues.syncStatus = SyncStatus;
        this.syncTemplateValues.timerSyncStatus = TimerSyncStatus;
        callback(this.syncTemplateValues);
      });
  }

  /**
   * Gets last online synchronization status
   */
  getLastOnlineSyncStatus() {
    return this.apiService.getGeneric(environment.offlineApiRoot + this.urls.getOnlineSyncStatus, true);
  }

  /**
   * Sets user's country
   * @param country country selected on the country select screen
   */
  selectCountry(country: string) {
    this.userData.UserInfo.CountryCode = country;
    window.localStorage.setItem('selectedCountry', country);
    this.dateFormat = dateFormats[this.userData.UserInfo.CountryCode] || dateFormats.US;
    this.dateTimeFormat = dateTimeFormats[this.userData.UserInfo.CountryCode] || dateTimeFormats.US;
    this.dsDateFormat = dsDateFormats[this.userData.UserInfo.CountryCode] || dsDateFormats.US;
    this.currencySymbol = currencies[this.userData.UserInfo.CountryCode] || currencies.US;
    this.weightSymbol = weightUnits[this.userData.UserInfo.CountryCode] || weightUnits.US;
    this.dateLocale = dateLocales[this.userData.UserInfo.CountryCode] || dateLocales.US;

    return this.apiService.post(this.urls.selectCountry, { countryCode: country });
  }

  userCountrySelected() {
    if (!this.userData.countrySelection) {
      return true;
    }

    return Boolean(window.localStorage.getItem('selectedCountry'));
  }

  isNSLocaleSelected() {
    if (!this.userData?.countrySelection && !this.userData?.UserInfo?.CountryCode) {
      return false;
    }

    return this.userData.UserInfo.CountryCode === CountryCode.NS;
  }
}
